import {
  createContext,
  useContext,
  useCallback,
  useState,
  type ReactNode,
} from "react";

export function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `id-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }
}

// ---------------- Status badge ----------------
export type StatusTone =
  | "draft"
  | "review"
  | "published"
  | "archived"
  | "live"
  | "offline"
  | "warning"
  | "neutral";

const toneStyles: Record<StatusTone, string> = {
  draft: "bg-secondary-100 text-secondary-700",
  review: "bg-accent-100 text-accent-700",
  published: "bg-green-100 text-green-700",
  archived: "bg-background-100 text-foreground-500",
  live: "bg-green-100 text-green-700",
  offline: "bg-background-100 text-foreground-500",
  warning: "bg-yellow-100 text-yellow-700",
  neutral: "bg-secondary-100 text-secondary-700",
};

export function StatusBadge({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${toneStyles[tone]}`}
    >
      {children}
    </span>
  );
}

export function statusTone(status: string): StatusTone {
  switch (status) {
    case "published":
    case "active":
    case "open":
    case "subscribed":
    case "live":
      return "published";
    case "review":
    case "scheduled":
    case "paused":
      return "review";
    case "draft":
    case "inactive":
    case "unsubscribed":
    case "unpublished":
      return "draft";
    case "archived":
    case "closed":
    case "expired":
    case "cancelled":
    case "completed":
      return "archived";
    default:
      return "neutral";
  }
}

// ---------------- Page header ----------------
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{title}</h1>
        {subtitle && <p className="text-sm text-foreground-600 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

// ---------------- Toast ----------------
interface ToastItem {
  id: number;
  message: string;
  type: "success" | "error" | "info";
}

const ToastContext = createContext<{
  show: (message: string, type?: "success" | "error" | "info") => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, type: "success" | "error" | "info" = "success") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  const iconFor = (type: ToastItem["type"]) =>
    type === "success" ? "ri-checkbox-circle-line" : type === "error" ? "ri-error-warning-line" : "ri-information-line";

  const barFor = (type: ToastItem["type"]) =>
    type === "success" ? "bg-green-500" : type === "error" ? "bg-accent-500" : "bg-primary-500";

  const iconColorFor = (type: ToastItem["type"]) =>
    type === "success" ? "text-green-600" : type === "error" ? "text-accent-600" : "text-primary-600";

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] space-y-2 max-w-xs">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="flex items-start gap-3 bg-white border border-background-200 rounded-lg p-3 shadow-lg"
          >
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className={`${iconFor(t.type)} text-lg ${iconColorFor(t.type)}`} />
            </span>
            <div className="min-w-0">
              <p className="text-sm text-foreground-900">{t.message}</p>
              <span className={`mt-1 block h-0.5 rounded-full ${barFor(t.type)}`} />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}

// ---------------- Modal ----------------
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-foreground-900/50" onClick={onClose} aria-hidden="true" />
      <div
        className={`relative bg-white rounded-lg w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8 flex flex-col max-h-[90vh]`}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-100">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">{title}</h2>
            {subtitle && <p className="text-xs text-foreground-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </header>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-background-100">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// ---------------- Confirm dialog ----------------
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-accent-500 rounded-md hover:bg-accent-600 whitespace-nowrap"
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-foreground-700">{message}</p>
    </Modal>
  );
}

// ---------------- Form field ----------------
export type FieldType = "text" | "textarea" | "select" | "toggle" | "date" | "time" | "tags";

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  help?: string;
  full?: boolean;
}

export function Field({
  def,
  value,
  onChange,
}: {
  def: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { type, label, placeholder, options, required, help, full } = def;

  const inputClass =
    "w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300";

  if (type === "toggle") {
    const on = Boolean(value);
    return (
      <div className={full ? "col-span-2" : ""}>
        <div className="flex items-center justify-between gap-3 py-2">
          <span className="text-sm font-medium text-foreground-800">
            {label}
            {required && <span className="text-accent-500 ml-0.5">*</span>}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => onChange(!on)}
            className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-primary-500" : "bg-background-300"}`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                on ? "translate-x-[22px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        {help && <p className="text-xs text-foreground-500 -mt-1">{help}</p>}
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className={full ? "col-span-2" : ""}>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          {label}
          {required && <span className="text-accent-500 ml-0.5">*</span>}
        </label>
        <textarea
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className={inputClass}
        />
        {help && <p className="text-xs text-foreground-500 mt-1">{help}</p>}
      </div>
    );
  }

  if (type === "select") {
    return (
      <div className={full ? "col-span-2" : ""}>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          {label}
          {required && <span className="text-accent-500 ml-0.5">*</span>}
        </label>
        <select
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        >
          <option value="">Select…</option>
          {options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {help && <p className="text-xs text-foreground-500 mt-1">{help}</p>}
      </div>
    );
  }

  if (type === "date" || type === "time") {
    return (
      <div className={full ? "col-span-2" : ""}>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          {label}
          {required && <span className="text-accent-500 ml-0.5">*</span>}
        </label>
        <input
          type={type}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
        {help && <p className="text-xs text-foreground-500 mt-1">{help}</p>}
      </div>
    );
  }

  if (type === "tags") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div className={full ? "col-span-2" : ""}>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          {label}
          {required && <span className="text-accent-500 ml-0.5">*</span>}
        </label>
        <input
          type="text"
          value={arr.join(", ")}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          placeholder={placeholder ?? "Comma-separated"}
          className={inputClass}
        />
        {help && <p className="text-xs text-foreground-500 mt-1">{help}</p>}
      </div>
    );
  }

  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-foreground-800 mb-1.5">
        {label}
        {required && <span className="text-accent-500 ml-0.5">*</span>}
      </label>
      <input
        type="text"
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputClass}
      />
      {help && <p className="text-xs text-foreground-500 mt-1">{help}</p>}
    </div>
  );
}